import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import SacredGeometry from '../components/SacredGeometry';
import ServiceCard from '../components/ServiceCard';
import AnimatedBackground from '../components/AnimatedBackground';

const Home = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const services = [
    {
      title: 'Constellations familiales systémiques',
      description: 'Approche profonde permettant de libérer les schémas inconscients et rétablir l\'équilibre dans les systèmes familiaux.',
    },
    {
      title: 'Communication animale',
      description: 'Entrer en lien avec l\'animal pour comprendre ses besoins, ses émotions et rétablir l\'harmonie.',
    },
    {
      title: 'Thérapie Quantique',
      description: 'Travail énergétique visant à réaligner les fréquences et favoriser le bien-être global.',
    },
    {
      title: 'Clairconnaissance',
      description: 'Accès intuitif à des informations subtiles pour guider et éclairer les situations de vie.',
    },
    {
      title: 'Radiesthésie',
      description: 'Utilisation des vibrations pour détecter, harmoniser et équilibrer les énergies.',
    },
  ];

  return (
    <div className="w-full">
      {/* HERO SECTION */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Multi-layer background gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-purple-900 via-purple-700 to-lavender-600"></div>
        <div className="absolute inset-0 bg-gradient-to-tr from-violet-800/50 via-transparent to-purple-600/30"></div>
        <div className="absolute inset-0 bg-gradient-to-bl from-transparent via-lavender-500/20 to-purple-800/40"></div>
        
        {/* Animated background elements */}
        <AnimatedBackground opacity={0.7} />
        
        {/* Sacred geometry background */}
        <SacredGeometry opacity={0.08} />
        
        {/* Content */}
        <div className="relative z-10 text-center px-6 py-20">
          <h1 className="text-5xl md:text-7xl lg:text-8xl font-serif font-bold text-white mb-8 animate-fade-in text-glow">
            Sylvaine Ben Klif
          </h1>
          
          {/* Hero Image */}
          <div className="flex justify-center mb-8 animate-fade-in-up" style={{ animationDelay: '100ms' }}>
            <Link to="/sylvaine" className="group cursor-pointer">
              <img
                src="/images/hero-image.jpg"
                alt="Sylvaine Ben Klif"
                className="w-64 h-64 md:w-96 md:h-96 lg:w-[28rem] lg:h-[28rem] object-cover rounded-full border-4 border-white/30 shadow-glow-lg group-hover:scale-105 group-hover:border-white/50 transition-all duration-500"
              />
            </Link>
          </div>
          
          <p className="text-xl md:text-2xl lg:text-3xl text-purple-100 mb-12 animate-fade-in-up font-light tracking-wide" style={{ animationDelay: '200ms' }}>
            Constellations familiales & communication animale
          </p>
          
          <Link
            to="/contact"
            className="inline-block px-8 py-4 bg-white/10 backdrop-blur-sm border-2 border-white/30 text-white rounded-full hover:bg-white hover:text-purple-900 transition-all duration-300 shadow-glow hover:shadow-glow-lg font-medium text-lg animate-fade-in-up"
            style={{ animationDelay: '300ms' }}
          >
            Me contacter
          </Link>
        </div>
      </section>

      {/* URGENT BANNER - ANIMAL PERDU */}
      <section className="relative py-8 px-6 bg-gradient-to-r from-purple-600 via-lavender-600 to-purple-600 border-y-2 border-purple-400/30">
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6 text-center md:text-left">
            {/* Icon + Text */}
            <div className="flex items-center gap-4">
              <div className="flex-shrink-0 w-12 h-12 bg-white/20 rounded-full flex items-center justify-center animate-pulse">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div>
                <p className="text-white font-semibold text-lg md:text-xl">
                  Votre animal est perdu ?
                </p>
                <p className="text-purple-100 text-sm md:text-base">
                  Je peux vous aider à le retrouver grâce à la communication animale
                </p>
              </div>
            </div>
            
            {/* CTA Button */}
            <Link
              to="/contact"
              className="flex-shrink-0 px-8 py-3 bg-white text-purple-700 rounded-full hover:bg-purple-50 transition-all duration-300 font-semibold text-base shadow-lg hover:shadow-xl transform hover:scale-105"
            >
              Me contacter en urgence
            </Link>
          </div>
        </div>
      </section>

      {/* SERVICES SECTION */}
      <section className="relative py-20 md:py-32 px-6 bg-gradient-to-b from-white to-purple-50/30">
        {/* Subtle background geometry */}
        <div className="absolute inset-0 overflow-hidden">
          <SacredGeometry opacity={0.02} />
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto">
          {/* Section header */}
          <div className="text-center mb-16 md:mb-20">
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-serif font-semibold text-purple-900 mb-6">
              Services
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-purple-400 to-lavender-500 mx-auto rounded-full"></div>
          </div>
          
          {/* Services grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10">
            {services.map((service, index) => (
              <ServiceCard
                key={index}
                index={index}
                title={service.title}
                description={service.description}
                delay={index * 100}
              />
            ))}
          </div>
        </div>
      </section>

      {/* QUOTE SECTION */}
      <section className="relative py-32 md:py-40 px-6 overflow-hidden">
        {/* Multi-layer background gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-purple-800 via-purple-600 to-lavender-500"></div>
        <div className="absolute inset-0 bg-gradient-to-tl from-violet-700/40 via-transparent to-purple-700/30"></div>
        
        {/* Animated background elements */}
        <AnimatedBackground opacity={0.5} />
        
        {/* Sacred geometry */}
        <SacredGeometry opacity={0.06} />
        
        {/* Content */}
        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-serif font-semibold text-white mb-12 text-glow leading-tight">
            Reconnecter aux liens invisibles
          </h2>
          
          <div className="space-y-6 text-purple-100 text-lg md:text-xl font-light">
            <p className="opacity-80 italic">
              "L'invisible tisse les liens les plus profonds"
            </p>
            <p className="opacity-80 italic">
              "Chaque être porte en lui la sagesse de ses ancêtres"
            </p>
            <p className="opacity-80 italic">
              "L'harmonie naît de l'écoute du cœur"
            </p>
          </div>
        </div>
      </section>

      {/* CALL TO ACTION SECTION */}
      <section className="relative py-20 md:py-24 px-6 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-serif font-semibold text-purple-900 mb-6">
            Prêt à commencer votre voyage intérieur ?
          </h2>
          <p className="text-gray-600 text-lg mb-8 max-w-2xl mx-auto">
            Prenez rendez-vous pour une séance personnalisée et découvrez le chemin vers l'harmonie et l'équilibre.
          </p>
          <Link
            to="/contact"
            className="inline-block px-10 py-4 bg-gradient-to-r from-purple-600 to-lavender-600 text-white rounded-full hover:shadow-glow-lg transition-all duration-300 font-medium text-lg transform hover:scale-105"
          >
            Contactez-moi
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
